import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Home, Inbox, Tag, Star, Ticket, Package, Image as ImageIcon, Activity, Wand2, Link as LinkIcon, Shield, RefreshCw, Menu, LogOut, BarChart3, Megaphone } from "lucide-react";
import { Logo } from "@/components/Logo";

const SECTIONS = [
  { label: "", items: [
    { key: "dashboard", name: "Home", icon: Home },
    { key: "orders", name: "Orders", icon: Inbox, count: "orders" },
    { key: "products", name: "Products", icon: Tag, count: "products" },
    { key: "reports", name: "Reports", icon: BarChart3 },
  ]},
  { label: "Sell", items: [
    { key: "brands", name: "Brands", icon: Star, count: "brands" },
    { key: "offers", name: "Discounts", icon: Ticket, count: "coupons" },
    { key: "sets", name: "Curated Sets", icon: Package },
    { key: "banner", name: "Homepage Banners", icon: ImageIcon },
  ]},
  { label: "Apps & Tools", items: [
    { key: "marketing", name: "Marketing Pixels", icon: Activity },
    { key: "studio", name: "AI Image Studio", icon: Wand2 },
    { key: "campaigns", name: "Campaign Kit", icon: Megaphone },
  ]},
  { label: "Settings", items: [
    { key: "integrations", name: "Integrations", icon: LinkIcon },
    { key: "users", name: "Users & Roles", icon: Shield },
  ]},
];

const TITLES = {
  dashboard: "Home", orders: "Orders", products: "Products", brands: "Brands", offers: "Discounts",
  sets: "Curated Sets", banner: "Homepage Banners", marketing: "Marketing Pixels", studio: "AI Image Studio",
  integrations: "Integrations", users: "Users & Roles", reports: "Reports", campaigns: "Campaign Kit",
};

const NavItems = ({ tab, setTab, counts, visibleKeys, onSync, syncing, closeMobile }) => (
  <nav className="flex-1 overflow-y-auto px-3 py-4 space-y-5">
    {SECTIONS.map((sec) => {
      const items = sec.items.filter((i) => visibleKeys.includes(i.key));
      if (!items.length) return null;
      return (
        <div key={sec.label || "main"}>
          {sec.label && <p className="px-3 mb-1.5 text-[10px] font-semibold uppercase tracking-wider text-gray-400">{sec.label}</p>}
          <div className="space-y-0.5">
            {items.map((item) => {
              const active = tab === item.key;
              return (
                <button
                  key={item.key}
                  onClick={() => { setTab(item.key); closeMobile?.(); }}
                  data-testid={`sidebar-nav-${item.key}`}
                  className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                    active ? "bg-white text-gray-900 shadow-sm border border-gray-200" : "text-gray-600 hover:bg-gray-200/50 border border-transparent"
                  }`}
                >
                  <item.icon size={16} className={active ? "text-[#E80071]" : "text-gray-400"} />
                  <span className="flex-1 text-left">{item.name}</span>
                  {item.count && counts[item.count] > 0 && (
                    <span className="text-[10px] font-semibold bg-gray-100 text-gray-500 rounded-full px-2 py-0.5">{counts[item.count]}</span>
                  )}
                </button>
              );
            })}
          </div>
        </div>
      );
    })}
    <div>
      <p className="px-3 mb-1.5 text-[10px] font-semibold uppercase tracking-wider text-gray-400">Actions</p>
      <button
        onClick={onSync}
        disabled={syncing}
        data-testid="sidebar-sync-btn"
        className="w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium text-gray-600 hover:bg-gray-200/50 transition-colors disabled:opacity-50"
      >
        <RefreshCw size={16} className={`text-gray-400 ${syncing ? "animate-spin" : ""}`} />
        {syncing ? "Syncing…" : "Sync Catalog"}
      </button>
    </div>
  </nav>
);

export const AdminShell = ({ tab, setTab, counts, visibleKeys, user, onLogout, onSync, syncing, syncStatus, headerActions, children }) => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const closeMobile = () => setMobileOpen(false);

  return (
    <div className="flex h-screen w-full bg-[#F6F6F7] overflow-hidden text-sm" data-testid="admin-shell">
      <aside className="hidden md:flex w-64 flex-col bg-[#F6F6F7] border-r border-gray-200 z-10">
        <div className="h-14 flex items-center px-5 border-b border-gray-200">
          <Logo size="sm" />
        </div>
        <NavItems tab={tab} setTab={setTab} counts={counts} visibleKeys={visibleKeys} onSync={onSync} syncing={syncing} />
      </aside>

      <AnimatePresence>
        {mobileOpen && (
          <>
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-[70] bg-black/50 md:hidden" onClick={closeMobile} />
            <motion.aside
              initial={{ x: "-100%" }} animate={{ x: 0 }} exit={{ x: "-100%" }} transition={{ type: "spring", bounce: 0, duration: 0.4 }}
              className="fixed top-0 left-0 bottom-0 z-[75] w-64 flex flex-col bg-[#F6F6F7] border-r border-gray-200 md:hidden"
              data-testid="sidebar-mobile"
            >
              <div className="h-14 flex items-center px-5 border-b border-gray-200">
                <Logo size="sm" />
              </div>
              <NavItems tab={tab} setTab={setTab} counts={counts} visibleKeys={visibleKeys} onSync={onSync} syncing={syncing} closeMobile={closeMobile} />
            </motion.aside>
          </>
        )}
      </AnimatePresence>

      <div className="flex-1 flex flex-col h-screen overflow-hidden">
        <header className="h-14 flex items-center justify-between px-4 sm:px-6 bg-white border-b border-gray-200 sticky top-0 z-40 shadow-sm">
          <div className="flex items-center gap-3">
            <button onClick={() => setMobileOpen(true)} className="md:hidden p-2 -ml-2 rounded-lg hover:bg-gray-100 text-gray-600" data-testid="sidebar-menu-btn" aria-label="Open menu">
              <Menu size={18} />
            </button>
            <h1 className="text-base font-semibold tracking-tight text-[#111827]" data-testid="admin-page-title">{TITLES[tab] || tab}</h1>
            {syncStatus?.last_run && (
              <span className="hidden lg:block text-xs text-gray-400" data-testid="sync-last-run">
                Catalog synced {new Date(syncStatus.last_run).toLocaleString("en-IN")}
              </span>
            )}
          </div>
          <div className="flex items-center gap-3">
            {headerActions}
            <span className="text-xs text-gray-400 hidden sm:block" data-testid="admin-user-email">{user.email}</span>
            <button onClick={onLogout} data-testid="admin-logout-btn" className="flex items-center gap-1.5 text-xs font-medium text-gray-500 hover:text-[#E80071] transition-colors px-2 py-1.5 rounded-lg hover:bg-gray-100">
              <LogOut size={13} /> Logout
            </button>
          </div>
        </header>
        <main className="flex-1 overflow-y-auto">
          <motion.div key={tab} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }} className="p-4 sm:p-6 max-w-7xl mx-auto w-full">
            {children}
          </motion.div>
        </main>
      </div>
    </div>
  );
};
